import { uniqueId } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { Field, useField } from 'formik';
import { XMarkIcon } from '@heroicons/react/24/outline';

import Button from '@dashboard/components/Button';
import Spinner from '@dashboard/components/Spinner';

import { formatBytes } from '@dashboard/services/formatting';
import logException from '@dashboard/services/logException';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import cropImage from '@dashboard/services/cropImage';
import { confirm } from '@dashboard/lib/confirm';

const SIZES = {
  logo: {
    width: 512,
    height: 512,
    aspect: 1,
  },
  variant_option: {
    width: 512,
    height: 512,
    aspect: 1,
  },
  favicon: {
    width: 32,
    height: 32,
    aspect: 1,
  },
  banner: {
    width: 1024,
    height: 512,
    aspect: 2,
  },
  background: {
    width: 1024,
    height: 1024,
    aspect: 2,
  },
};

const ImageUpload = ({
  image,
  name,
  onUpdate,
  crop,
  previewSize = 'logo',
  variant = 'default',
}) => {
  const { sellerId } = useAuth();
  const [file, setFile] = useState(image);
  const { uploadFile } = useFileQueries();

  useEffect(() => {
    setFile(image);
  }, [image]);

  const onDrop = useCallback(
    async acceptedFiles => {
      let acceptedFileToSet = Object.assign(acceptedFiles[0], {
        key: uniqueId(),
        url: URL.createObjectURL(acceptedFiles[0]),
        isUploading: true,
        isUploaded: false,
      });

      async function upload(file) {
        try {
          const { data, error } = await uploadFile({
            file,
            bucket: 'images',
            sellerId,
          });

          if (error) {
            throw error;
          }

          const mediaItem = {
            key: file.key,
            file: data._id,
            url: data.location,
            name: data.name,
            size: data.size,
            width: data.width,
            height: data.height,
          };

          setFile(mediaItem);
          onUpdate(mediaItem);
        } catch (error) {
          console.log(error);
          logException({ message: 'Failed to upload file', error });
          setFile(null);
        }
      }

      let img = new Image();
      img.src = window.URL.createObjectURL(acceptedFileToSet);
      img.onload = async () => {
        acceptedFileToSet.width = img.width;
        acceptedFileToSet.height = img.height;

        if (crop && acceptedFileToSet.type !== 'image/gif') {
          const croppedFile = await cropImage(img, crop, acceptedFileToSet);

          const existingFile = acceptedFileToSet;

          acceptedFileToSet = croppedFile;
          acceptedFileToSet.height = acceptedFileToSet.height || existingFile.height;
          acceptedFileToSet.width = acceptedFileToSet.width || existingFile.width;
          acceptedFileToSet.isUploaded = existingFile.isUploaded;
          acceptedFileToSet.isUploading = existingFile.isUploading;
          acceptedFileToSet.key = existingFile.key;
          acceptedFileToSet.path = existingFile.path;
          acceptedFileToSet.url = URL.createObjectURL(croppedFile);
        }

        setFile(acceptedFileToSet);

        await upload(acceptedFileToSet);
      };
    },
    [file],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: 'image/jpeg, image/png, image/jpg, image/gif, image/webp, image/svg+xml',
    maxSize: 10000000,
  });

  async function onDeleteFile() {
    setFile(null);
    onUpdate(null);
  }

  return (
    <div className="h-full w-full">
      {file && (
        <File
          file={file}
          onDeleteFile={() => onDeleteFile(file, file.file)}
          previewSize={previewSize}
          isLast
          variant={variant}
        />
      )}

      {!file && (
        <div
          {...getRootProps()}
          className={`h-full w-full transition-all border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:border-chocolate-600 focus:border-solid hover hover:border-chocolate-1000 hover:border-solid ${
            isDragActive ? 'border-chocolate-600 border-solid' : 'border-dashed'
          }`}
        >
          <input name={name} id={name} {...getInputProps()} />

          <div
            className={clsx({
              'flex justify-start p-3': variant === 'default',
              'h-full w-full flex items-center justify-center': variant === 'minimal',
            })}
          >
            <div
              className={clsx({
                'mr-2 h-12 w-12': variant === 'default',
                'h-8 w-8': variant === 'minimal',
              })}
            >
              <svg
                className="text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {variant === 'default' && (
              <div className="">
                <div className="flex text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-chocolate-600 hover:text-chocolate-600 focus-within:outline-none"
                  >
                    <span>Upload image</span>
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-sm text-gray-500">
                  Recommended size is {SIZES[previewSize].width} x {SIZES[previewSize].height}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {fileRejections.map(({ file, errors }) => (
        <FileRejection key={file.path} file={file} errors={errors} />
      ))}
    </div>
  );
};

function FileRejection({ file, errors }) {
  if (!file.type.startsWith('image/')) {
    return (
      <div className="my-1 text-red-600" key={file.path}>
        {file.name} is not an image
      </div>
    );
  }

  if (file.size > 10000000) {
    return (
      <div className="my-1 text-red-600">
        {`${file.name} is too big (${formatBytes(file.size)}). Max size: 10MB`}
      </div>
    );
  }

  return errors.map(err => (
    <div key={err.key} className="my-1 text-red-600">
      {err.message}
    </div>
  ));
}

function File({ file, onDeleteFile, isLast, previewSize, variant }) {
  async function onDelete() {
    const del = await confirm('Delete this image?', {
      confirm: 'Delete',
    });

    if (del) {
      try {
        await onDeleteFile(file);
      } catch (error) {
        window.alert('Unable to delete image');
      }
    }
  }

  const fileImage = (() => {
    if (file.url.includes('blob:')) {
      return { src: file.url, srcSet: undefined };
    }

    const { width, height } = (() => {
      switch (previewSize) {
        case 'background':
        case 'variant_option': {
          return {
            width: 64,
            height: 64,
          };
        }
        case 'ticket_type': {
          return {
            width: 71,
            height: 71,
          };
        }
        case 'banner': {
          return {
            width: 256,
            height: 128,
          };
        }
        case 'logo': {
          return {
            width: 80,
            height: 80,
          };
        }
        case 'favicon': {
          return {
            height: 24,
            width: 24,
          };
        }
        default: {
          return {
            width: 64,
            height: 64,
          };
        }
      }
    })();

    return getImageSrcAndSrcSet({ url: file.url, width, height });
  })();

  return (
    <div
      className={clsx(`${!isLast ? 'border-b' : ''}`, {
        'mb-4 flex flex-row gap-3': variant === 'default',
        'h-full w-full flex items-center justify-center': variant === 'minimal',
      })}
    >
      <div
        className={clsx('overflow-hidden relative', {
          'rounded-full shadow w-20 h-20 flex items-center justify-center':
            previewSize === 'logo' || previewSize === 'favicon',
          'flex-grow-0 flex-shrink-0': variant === 'default',
          'w-full h-full': variant === 'minimal',
        })}
      >
        <img
          className={clsx('object-cover text-transparent rounded-md', {
            'w-16 h-16': previewSize === 'background' || previewSize === 'variant_option',
            'w-[71px] h-[71px]': previewSize === 'ticket_type',
            'w-64 h-32': previewSize === 'banner',
            'w-20 h-20': previewSize === 'logo',
            'w-6 h-6': previewSize === 'favicon',
          })}
          src={fileImage.src}
          srcSet={fileImage.srcSet}
          alt={file.name}
        />

        {variant === 'minimal' && (
          <>
            {file.isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                <Spinner width="24px" height="24px" />
              </div>
            )}

            <div className="absolute top-px right-px flex items-center justify-center">
              <button
                onClick={onDelete}
                aria-label="delete"
                type="button"
                className="rounded-full p-px bg-gray-100/50 hover:bg-gray-100/100 text-black transition-opacity duration-300"
              >
                <XMarkIcon className="w-4 h-4 " />
              </button>
            </div>
          </>
        )}
      </div>

      {variant === 'default' && (
        <div className="flex flex-col gap-2">
          {file.isUploading && <Spinner width="24px" height="24px" />}

          <div className="mt-auto flex gap-2">
            <Button appearance="subtle" onClick={onDelete} aria-label="delete" type="button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                height="16px"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const ImageField = props => {
  // eslint-disable-next-line no-unused-vars
  const [field, meta, helpers] = useField(props);

  const { setValue } = helpers;

  const onChange = value => {
    setValue(value);

    if (props.onChange) {
      props.onChange(value);
    }
  };

  return (
    <Field {...props} as={ImageUpload} onUpdate={onChange} image={field.value} name={field.name} />
  );
};

export default ImageUpload;
